<template>
  <!-- Container principal split-screen -->
  <div class="flex min-h-screen">
    <!-- Lado izquierdo: Formulario -->
    <div class="w-full lg:w-1/2 flex flex-col justify-center px-8 sm:px-12 lg:px-16 xl:px-20 bg-white">
      <!-- Logo con mejor espaciado y tamaño -->
      <div class="mb-6">
        <img class="h-20 w-auto" alt="Efibox Logo" :src="LogoEfibox">
      </div>

      <!-- Encabezado del formulario -->
      <div class="mb-8">
        <h1 class="text-4xl font-bold text-gray-900 mb-2">Iniciar Sesión</h1>
        <p class="text-gray-600">Introduzca su email y contraseña para iniciar sesión!</p>
        <!-- Mensaje de error global del formulario -->
        <div v-if="loginError" class="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong class="font-bold">Error: </strong>
          <span class="block sm:inline">{{ loginError }}</span>
          <span @click="loginError = null" class="absolute top-0 bottom-0 right-0 px-4 py-3 cursor-pointer">
            <svg class="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
              <title>Close</title>
              <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" />
            </svg>
          </span>
        </div>
      </div>

      <!-- Formulario -->
      <form @submit.prevent="handleLogin" class="space-y-6">
        <!-- Campo Email -->
        <div>
          <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
            Email*
          </label>
          <input v-model="email" required class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" type="email" name="email" id="email" placeholder="mail@efintel.cl" :disabled="isLoading">
        </div>

        <!-- Campo Password -->
        <div>
          <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
            Contraseña*
          </label>
          <div class="relative">
            <input v-model="password" required :type="showPassword ? 'text' : 'password'" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors pr-12" name="password" id="password" placeholder="Ingrese su contraseña" :disabled="isLoading">

            <!-- Botón mostrar/ocultar contraseña -->
            <button type="button" @click="togglePasswordVisibility" class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600" :disabled="isLoading">
              <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21">
                </path>
              </svg>
              <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                </path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Opciones adicionales -->
        <!-- <div class="flex items-center justify-between">
          <a 
            href="#" 
            @click.prevent="handleForgotPassword"
            class="text-sm text-blue-600 hover:text-blue-500 font-medium">
            Has olvidado tu contraseña?
          </a>
        </div> -->
        <!-- Botón de login -->
        <button type="submit" class="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed" :disabled="isLoading || !isFormValid">

          <!-- Spinner de carga -->
          <svg v-if="isLoading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
            </path>
          </svg>
          {{ isLoading ? 'Verificando tus credenciales ...' : 'Iniciar sesión' }}
        </button>
      </form>
    </div>

    <!-- Lado derecho: Imagen/Ilustración - fondo por ahora blanco, ya que la imagen no tiene alta calidad -->
    <div class="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-50 to-white items-center justify-center p-8">
      <div class="w-full h-full flex items-center justify-center">
        <img :src="illustrationImage" alt="Efibox - Diagrama de características de la plataforma" class="w-full h-auto max-w-4xl object-contain">
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/services/auth/auth.js'
import LogoEfibox from '@/assets/logoEfibox.png'
import illustrationImage from '@/assets/fondo-efibox-login.png'

export default {
  name: 'LoginForm',

  props: {
    externalLoading: {
      type: Boolean,
      default: false
    },
  },

  emits: ['login-success', 'login-error'],

  data() {
    return {
      // Campos del formulario
      email: '',
      password: '',
      rememberMe: false,
      showPassword: false,

      loginError: null, // Para mostrar mensajes de error al usuario
      // Estado interno
      isSubmitting: false,

      LogoEfibox,
      illustrationImage
    }
  },

  computed: {
    isFormValid() {
      return this.email.length > 0 &&
        this.password.length > 0 &&
        this.isValidEmail(this.email)
    },

    isLoading() {
      return this.isSubmitting || this.externalLoading
    }
  },

  methods: {
    async handleLogin() {
      if (!this.isFormValid) return

      this.isSubmitting = true

      try {
        const response = await AuthService.login({
          email: this.email,
          password: this.password,
          remember: this.rememberMe
        })

        this.$emit('login-success', response)
        this.resetForm()

      } catch (error) {
        console.error('Error en login:', error)
        this.$emit('login-error', {
          message: this.getErrorMessage(error),
          type: 'authentication'
        })

      } finally {
        this.isSubmitting = false
      }
    },

    handleForgotPassword() {
      console.log('Forgot password clicked')
      alert('Funcionalidad de recuperación de contraseña en desarrollo')
    },

    handleSignUp() {
      console.log('Sign up clicked')
      alert('Funcionalidad de registro en desarrollo')
    },

    togglePasswordVisibility() {
      this.showPassword = !this.showPassword
    },

    resetForm() {
      this.email = ''
      this.password = ''
      this.rememberMe = false
      this.showPassword = false
      this.loginError = null // Limpiar errores al resetear el formulario
    },

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    getErrorMessage(error) {
      if (error.message) {
        return error.message
      }

      const errorMessages = {
        'Invalid credentials': 'Email o contraseña incorrectos',
        'User not found': 'Usuario no encontrado',
        'Account disabled': 'Cuenta deshabilitada. Contacte al administrador',
        'Too many attempts': 'Demasiados intentos. Intente más tarde',
        'Network Error': 'Error de conexión. Verifique su internet'
      }

      return errorMessages[error.toString()] || 'Error inesperado. Intente nuevamente'
    }
  },

  mounted() {
    this.$nextTick(() => {
      const emailInput = document.getElementById('email')
      if (emailInput) {
        emailInput.focus()
      }
    })
  }
}
</script>

<style scoped></style>